// src/components/Auth/RegisterForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import SEOHead from '../SEO/SEOHead';
import { useThemeContext } from '../../contexts/ThemeContext';
import {
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    InputAdornment,
    IconButton,
    CircularProgress,
    Divider,
    useTheme,
    alpha,
    useMediaQuery,
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import GoogleLoginButton from './GoogleLoginButton';

export const RegisterForm = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { register, error: authError, setError, loading } = useAuth();
    const theme = useTheme();
    const themeContext = useThemeContext();
    const themeMode = themeContext?.themeMode || 'light';
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Animation states
    const [showForm, setShowForm] = useState(false);

    // Form state management with support for all required registration fields
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [registrationAttempted, setRegistrationAttempted] = useState(false);

    // Trigger animation on component mount
    useEffect(() => {
        setShowForm(true);
    }, []);

    // Comprehensive form validation that checks all registration fields
    const validateForm = () => {
        const errors = {};

        if (!formData.name.trim()) {
            errors.name = t('validation.name', 'Name is required');
        } else if (formData.name.trim().length < 2) {
            errors.name = t('validation.nameLength', 'Name must be at least 2 characters long');
        }

        if (!formData.email) {
            errors.email = t('validation.email', 'Please enter a valid email address');
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
            errors.email = t('validation.email', 'Please enter a valid email address');
        }

        if (!formData.password) {
            errors.password = t('validation.password', 'Password must be at least 8 characters long');
        } else if (formData.password.length < 6) {
            errors.password = t('validation.password', 'Password must be at least 8 characters long');
        }

        if (!formData.confirmPassword) {
            errors.confirmPassword = t('validation.passwordMatch', 'Passwords do not match');
        } else if (formData.password !== formData.confirmPassword) {
            errors.confirmPassword = t('validation.passwordMatch', 'Passwords do not match');
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle form submission with proper error handling
    const handleSubmit = async (e) => {
        e.preventDefault();
        setRegistrationAttempted(true);

        if (!validateForm()) {
            return;
        }

        try {
            // Pass only the required data to the register function
            const result = await register({
                name: formData.name.trim(),
                email: formData.email.toLowerCase(),
                password: formData.password
            });

            // Redirect to email verification pending page
            if (result?.requiresEmailVerification) {
                navigate('/email-pending', { state: { email: result.email, name: result.name } });
                return;
            }

            // Navigate to dashboard on successful registration (legacy flow)
            navigate('/dashboard');
        } catch (err) {
            console.error('Registration failed:', err);
        }
    };

    // Handle input changes with validation clearing
    const handleInputChange = (field) => (e) => {
        setFormData(prev => ({
            ...prev,
            [field]: e.target.value
        }));

        // Clear validation errors when user starts typing
        if (validationErrors[field]) {
            setValidationErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleSwitchToLogin = () => {
        navigate('/login');
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            py: 4,
            position: 'relative',
            overflow: 'hidden',
            background: themeMode === 'dark'
                ? 'linear-gradient(160deg, #0a0f1e 0%, #0d1f2d 60%, #0a1a12 100%)'
                : 'linear-gradient(160deg, #f0fdf4 0%, #ecfdf5 60%, #f0f9ff 100%)',
        }}>
            <SEOHead
                title="Create Free Account"
                description="Create your free SoldiKeeper account. Track expenses, split bills, and manage budgets in minutes. No credit card required."
                canonical="/register"
            />
            {/* Ambient blobs */}
            <Box sx={{
                position: 'absolute', width: 400, height: 400, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)',
                top: '-10%', right: '-10%', pointerEvents: 'none',
            }} />
            <Box sx={{
                position: 'absolute', width: 300, height: 300, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)',
                bottom: '5%', left: '-8%', pointerEvents: 'none',
            }} />

            {/* Back to landing */}
            <Box sx={{ position: 'absolute', top: 20, left: 20 }}>
                <Button
                    onClick={() => navigate('/')}
                    size="small"
                    sx={{
                        textTransform: 'none', color: 'text.secondary', fontSize: '0.8rem',
                        '&:hover': { color: '#10b981' },
                    }}
                >
                    ← SoldiKeeper
                </Button>
            </Box>

            {/* Form container */}
            <Box sx={{ width: '100%', maxWidth: 400, zIndex: 1 }}>
                {/* Logo + title */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Box
                        component="img"
                        src="/assets/images/logo.png"
                        alt="SoldiKeeper"
                        onError={(e) => { e.target.style.display = 'none'; }}
                        sx={{ width: 48, height: 48, borderRadius: 2, mb: 1.5 }}
                    />
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                        {t('auth.register.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('auth.register.subtitle')}
                    </Typography>
                </Box>

                {authError && registrationAttempted && (
                    <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                        {authError}
                    </Alert>
                )}

                {/* Google OAuth */}
                <Box sx={{ mb: 2.5 }}>
                    <GoogleLoginButton variant="outlined" fullWidth size="large" />
                </Box>

                {/* Divider */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.disabled" sx={{ mx: 2 }}>
                        {t('auth.register.orEmail', 'or continue with email')}
                    </Typography>
                    <Divider sx={{ flex: 1 }} />
                </Box>

                {/* Registration form */}
                <form onSubmit={handleSubmit} noValidate>
                    <TextField
                        fullWidth
                        label={t('auth.register.name', 'Full Name')}
                        value={formData.name}
                        onChange={handleInputChange('name')}
                        error={!!validationErrors.name}
                        helperText={validationErrors.name}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2.5,
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10b981', borderWidth: 2 },
                            },
                        }}
                    />

                    <TextField
                        fullWidth
                        label={t('auth.register.email')}
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange('email')}
                        error={!!validationErrors.email}
                        helperText={validationErrors.email}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2.5,
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10b981', borderWidth: 2 },
                            },
                        }}
                    />

                    <TextField
                        fullWidth
                        label={t('auth.register.password')}
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleInputChange('password')}
                        error={!!validationErrors.password}
                        helperText={validationErrors.password}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2.5,
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10b981', borderWidth: 2 },
                            },
                        }}
                    />

                    <TextField
                        fullWidth
                        label={t('auth.register.confirmPassword')}
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={handleInputChange('confirmPassword')}
                        error={!!validationErrors.confirmPassword}
                        helperText={validationErrors.confirmPassword}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                                        {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            mb: 3,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2.5,
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10b981', borderWidth: 2 },
                            },
                        }}
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={loading}
                        endIcon={!loading && <ArrowForwardIcon />}
                        disableElevation
                        sx={{
                            height: 50, borderRadius: 2.5, textTransform: 'none',
                            fontWeight: 700, fontSize: '1rem',
                            bgcolor: '#10b981',
                            '&:hover': { bgcolor: '#059669' },
                            '&:active': { transform: 'scale(0.98)' },
                            transition: 'all 0.2s ease',
                            mb: 3,
                        }}
                    >
                        {loading ? <CircularProgress size={22} color="inherit" /> : t('auth.register.submit')}
                    </Button>

                    <Typography variant="body2" color="text.secondary" textAlign="center">
                        {t('auth.register.hasAccount')}{' '}
                        <Box
                            component="span"
                            onClick={handleSwitchToLogin}
                            sx={{ color: '#10b981', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        >
                            {t('auth.register.signIn')}
                        </Box>
                    </Typography>
                </form>
            </Box>
        </Box>
    );
};

export default RegisterForm;